import { usePdfDownloadContext } from "@hooks/context/pdfDownloadContext";
import { LoadingStatusContent } from "@ui/molecules/Loaders/LoadingStatusContent";
import { toast, Id } from "react-toastify";
import { FileGenerationStatus } from "catchment-common/entities/fileGeneration";

/** Renders toast content that subscribes to context for live progress updates */
function FileDownloadToastContent({ downloadId }: { downloadId: string }) {
  const { downloads } = usePdfDownloadContext();
  const download = downloads.find((d) => d.id === downloadId);

  if (!download) return null;

  return (
    <LoadingStatusContent
      message={getStatusMessage(download.downloadStep)}
      title={download.title}
      value={download.downloadProgress}
    />
  );
}

const activeToasts = new Map<string, Id>();

function getStatusMessage(downloadStep: FileGenerationStatus): string {
  switch (downloadStep) {
    case FileGenerationStatus.Fetching:
      return "Requesting...";
    case FileGenerationStatus.Collecting:
      return "Preparing data...";
    case FileGenerationStatus.Generating:
      return "Generating documents...";
    case FileGenerationStatus.Zipping:
      return "Compressing...";
    case FileGenerationStatus.Generated:
      return "Generated...";
    case FileGenerationStatus.Downloading:
      return "Downloading...";
    case FileGenerationStatus.Error:
      return "Error...";
    default:
      return "Requesting...";
  }
}

export const showFileDownloadToast = (
  downloadStep: FileGenerationStatus,
  downloadId: string
) => {
  // If the download is completed (Idle), close the toast and remove it from the Map
  if (downloadStep === FileGenerationStatus.Idle) {
    const toastId = activeToasts.get(downloadId);
    if (toastId) {
      toast.dismiss(toastId);
      activeToasts.delete(downloadId);
    }
    return;
  }

  // If there’s an error, show an error toast and close the existing one
  if (downloadStep === FileGenerationStatus.Error) {
    const toastId = activeToasts.get(downloadId);
    if (toastId) {
      toast.dismiss(toastId);
      activeToasts.delete(downloadId);
    }
    toast.error("Failed to download file", {
      autoClose: 5000,
      position: "bottom-right"
    });
    return;
  }

  const existingToastId = activeToasts.get(downloadId);

  if (!existingToastId) {
    // Create a new toast - FileDownloadToastContent subscribes to context for live progress updates
    const toastId = toast(
      <FileDownloadToastContent downloadId={downloadId} />,
      {
        autoClose: false,
        className: "custom-notification",
        closeOnClick: false,
        containerId: "bottom-right",
        hideProgressBar: true,
        icon: false,
        position: "bottom-right",
        toastId: `file-download-progress-${downloadId}`
      }
    );
    activeToasts.set(downloadId, toastId);
  }
};

export const dismissFileDownloadToast = (downloadId?: string) => {
  if (downloadId) {
    // Close a specific toast
    const toastId = activeToasts.get(downloadId);
    if (toastId) {
      toast.dismiss(toastId);
      activeToasts.delete(downloadId);
    }
  } else {
    // Close all toasts (for backward compatibility)
    activeToasts.forEach((toastId) => {
      toast.dismiss(toastId);
    });
    activeToasts.clear();
  }
};
